import { Phone, MessageCircle, Clock, MapPin, Zap, ShieldCheck, CalendarCheck } from "lucide-react";
import { HERO } from "@/constants/testIds";
import { telHref, waHref, PHONE, HERO_BG } from "@/constants/site";
import useCallTracking from "@/hooks/useCallTracking";
import useEventTracking from "@/hooks/useEventTracking";
import { useT } from "@/i18n/LanguageContext";
import MiniReviewTicker from "@/components/MiniReviewTicker";

export default function Hero() {
  const trackCall = useCallTracking();
  const trackEvent = useEventTracking();
  const { t } = useT();
  return (
    <section
      id="top"
      data-testid={HERO.section}
      className="relative min-h-screen flex items-center overflow-hidden bg-white hero-with-policy"
    >
      {/* Background — van photo; desktop right-aligned (text sits on the left),
         mobile centered so the whole van is visible in portrait viewports. */}
      <div
        className="absolute inset-0 hero-van-bg"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 hero-gradient-overlay" aria-hidden="true" />

      {/* Subtle blue accent bar */}
      <div className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-72 bg-[#1E63FF]" aria-hidden="true" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28 w-full">
        <div className="max-w-3xl">
          {/* Badge */}
          <div
            data-testid={HERO.badge}
            className="inline-flex items-center gap-2.5 px-3.5 py-2 rounded-full bg-[#0A0A0A] text-white text-xs font-semibold fade-up"
            style={{ animationDelay: "0.05s" }}
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#1E63FF] opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#1E63FF]"></span>
            </span>
            <span className="tracking-wider-2 uppercase">{t.hero.badge}</span>
          </div>

          {/* Heading */}
          <h1
            data-testid={HERO.heading}
            className="font-display font-semibold text-4xl sm:text-5xl lg:text-7xl xl:text-[5.5rem] tracking-tight leading-[0.95] mt-6 sm:mt-7 text-[#0A0A0A] fade-up"
            style={{ animationDelay: "0.15s" }}
          >
            {t.hero.line1}<br />
            {t.hero.line2}<br />
            <span className="text-[#1E63FF] accent-underline">{t.hero.line3}</span>
          </h1>

          {/* Sub */}
          <p
            data-testid={HERO.subheading}
            className="mt-5 sm:mt-7 text-base sm:text-xl text-neutral-700 font-body leading-relaxed max-w-2xl fade-up"
            style={{ animationDelay: "0.25s" }}
          >
            {t.hero.sub}{" "}
            <strong className="text-[#0A0A0A] font-semibold">{t.hero.subBold}</strong>.
          </p>

          {/* CTAs */}
          <div className="mt-7 sm:mt-9 flex flex-wrap items-center gap-3 fade-up" style={{ animationDelay: "0.35s" }}>
            <a
              data-testid={HERO.callBtn}
              href={telHref}
              onClick={trackCall("hero")}
              className="btn-blue h-12 sm:h-14 px-6 sm:px-7 rounded-full inline-flex items-center gap-2.5 sm:gap-3 text-sm sm:text-base font-semibold pulse-blue"
            >
              <Phone className="w-5 h-5" strokeWidth={2.2} />
              {t.hero.callBtn} {PHONE}
            </a>
            <a
              data-testid="hero-book-btn"
              href="#book"
              onClick={trackEvent("book_click", "hero")}
              className="btn-black h-12 sm:h-14 px-6 sm:px-7 rounded-full inline-flex items-center gap-2.5 sm:gap-3 text-sm sm:text-base font-semibold"
            >
              <CalendarCheck className="w-5 h-5" strokeWidth={2.2} />
              {t.hero.bookBtn}
            </a>
            <a
              data-testid={HERO.whatsappBtn}
              href={waHref()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={trackEvent("whatsapp", "hero")}
              className="btn-outline-black h-12 sm:h-14 px-6 sm:px-7 rounded-full inline-flex items-center gap-2.5 sm:gap-3 text-sm sm:text-base font-semibold"
            >
              <MessageCircle className="w-5 h-5" strokeWidth={2.2} />
              {t.hero.whatsappBtn}
            </a>
          </div>

          {/* Mini review ticker */}
          <div className="mt-5 sm:mt-6 fade-up" style={{ animationDelay: "0.42s" }}>
            <MiniReviewTicker />
          </div>

          {/* Trust strip */}
          <div className="mt-9 sm:mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-5 max-w-2xl fade-up" style={{ animationDelay: "0.5s" }}>
            <TrustItem icon={Zap} label={t.hero.trust.avgArrivalLabel} value={t.hero.trust.avgArrivalValue} />
            <TrustItem icon={Clock} label={t.hero.trust.openNowLabel} value={t.hero.trust.openNowValue} />
            <TrustItem icon={MapPin} label={t.hero.trust.coverageLabel} value={t.hero.trust.coverageValue} />
            <TrustItem icon={ShieldCheck} label={t.hero.trust.insuredLabel} value={t.hero.trust.insuredValue} />
          </div>
        </div>
      </div>

      {/* Bottom edge */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-black/10" />
    </section>
  );
}

function TrustItem({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-md bg-[#0A0A0A] text-white flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </div>
      <div>
        <div className="overline text-neutral-500">{label}</div>
        <div className="text-sm font-semibold text-[#0A0A0A] mt-1">{value}</div>
      </div>
    </div>
  );
}
